import { TYPESENSE_PRICING_COLLECTION } from "../config";

// Product data and per-store pricing now live in separate Typesense collections
// (`*_products` and `*_pricing`). Products are queried on their own and the
// pricing for the current page is fetched in a follow-up query against the
// pricing collection, then flattened onto the `{store_id}_price` style keys the
// inventory tables already read. The join is still used in `filter_by` to
// narrow results to what a store carries, but never in `include_fields` — that
// would pull back a pricing row for every store carrying the product.

// Product-level fields the inventory views render or filter on. Add a field
// here if a column starts coming back blank.
const PRODUCT_FIELDS = [
  "id",
  "sku_id",
  "title",
  "subtitle",
  "volume_label",
  "image",
  "default_category",
  "categories",
  "company",
  "type",
  "upc_code",
  "recipe",
  "disabled",
  "retailer_disabled",
];

// Field on the pricing collection that points back at the product.
const PRICING_SKU_FIELD = "sku_id";

const PRICING_FIELDS = [
  "store_id",
  "price",
  "buy_price",
  "qty",
  "package_item_price",
  "package_qty",
];

// Typesense caps per_page at 250, so the page size of any view relying on the
// pricing lookup needs to stay at or under that.
const MAX_PRICING_ROWS = 250;

// Sku ids get interpolated into a filter expression, so anything that could
// break the parse is dropped from the lookup rather than risking the whole
// query. A skipped id just means that row renders without pricing.
const FILTER_SAFE_SKU = /^[A-Za-z0-9_.-]+$/;

export const PRODUCT_INCLUDE_FIELDS = PRODUCT_FIELDS.join(",");

/**
 * Build a `filter_by` clause against the joined pricing collection.
 * Conditions are ANDed inside a single clause so they all have to hold for the
 * same pricing row, e.g. `$production_pricing(store_id:=SEA307 && price:>0)`.
 * Referencing the join in `filter_by` makes the query an inner join, so only
 * products the store actually carries come back.
 */
export const pricingJoinFilter = (...conditions) =>
  `$${TYPESENSE_PRICING_COLLECTION}(${conditions.join(" && ")})`;

const isPresent = (value) => typeof value === "number" || typeof value === "string";

// The table calls `.toFixed()` on prices, so strings need to be coerced rather
// than passed through as-is.
const toNumber = (value) => {
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? undefined : parsed;
  }
  return value;
};

/** Copy one pricing row onto the denormalized store keys of a product hit. */
const applyPricingRow = (target, row) => {
  const storeId = row.store_id;
  if (isPresent(row.price)) {
    target[`${storeId}_price`] = toNumber(row.price);
  }
  if (isPresent(row.buy_price)) {
    target[`${storeId}_buy_price`] = toNumber(row.buy_price);
  }
  if (isPresent(row.qty)) {
    target[`${storeId}_qty`] = row.qty;
  }
  if (row.package_item_price) {
    target[`${storeId}_package_item_price`] = toNumber(row.package_item_price);
  }
  if (row.package_qty) {
    target[`${storeId}_package_qty`] = row.package_qty;
  }
  return target;
};

const extractJoinedPricingRows = (document) => {
  let rows = document[TYPESENSE_PRICING_COLLECTION] || document.pricing;
  if (!rows) {
    return [];
  }
  if (!Array.isArray(rows)) {
    rows = [rows];
  }
  return rows.filter((row) => row && row.store_id);
};

/**
 * Flatten pricing rows that arrived nested on the hit. Only relevant if a query
 * asks for the pricing join in `include_fields`; mirrors what MenuService does
 * server side so both response shapes hydrate the same way.
 */
export const hydrateJoinedPricing = (document) => {
  if (!document) {
    return document;
  }
  const rows = extractJoinedPricingRows(document);
  if (!rows.length) {
    return document;
  }

  const hydrated = { ...document };
  rows.forEach((row) => applyPricingRow(hydrated, row));
  delete hydrated[TYPESENSE_PRICING_COLLECTION];
  delete hydrated.pricing;
  return hydrated;
};

/**
 * Fetch this store's pricing for a page of products, keyed by sku.
 * Returns null when the lookup can't be made or fails, so callers fall back to
 * rendering the products without pricing rather than failing the whole search.
 */
const fetchStorePricing = async (typesenseClient, storeId, skuIds) => {
  if (!typesenseClient || !storeId || !skuIds.length) {
    return null;
  }
  try {
    const response = await typesenseClient
      .collections(TYPESENSE_PRICING_COLLECTION)
      .documents()
      .search({
        q: "*",
        filter_by: `store_id:=${storeId} && ${PRICING_SKU_FIELD}:=[${skuIds.join(",")}]`,
        include_fields: [PRICING_SKU_FIELD, ...PRICING_FIELDS].join(","),
        per_page: Math.min(skuIds.length, MAX_PRICING_ROWS),
      });
    const rowsBySku = new Map();
    (response.hits || []).forEach(({ document }) => {
      if (document && document[PRICING_SKU_FIELD]) {
        rowsBySku.set(document[PRICING_SKU_FIELD], document);
      }
    });
    return rowsBySku;
  } catch (error) {
    console.error("Failed to fetch store pricing for search results:", error);
    return null;
  }
};

const hydrateResult = async (result, request, typesenseClient) => {
  if (!result || !Array.isArray(result.hits) || !result.hits.length) {
    return result;
  }

  // Pricing already came back nested on the hits.
  if (result.hits.some((hit) => extractJoinedPricingRows(hit).length)) {
    return { ...result, hits: result.hits.map(hydrateJoinedPricing) };
  }

  const storeId = request && request.params && request.params.pricingStoreId;
  if (!storeId) {
    return result;
  }

  const skuIds = result.hits
    .map((hit) => hit.sku_id)
    .filter((skuId) => skuId && FILTER_SAFE_SKU.test(skuId));
  const rowsBySku = await fetchStorePricing(typesenseClient, storeId, skuIds);
  if (!rowsBySku || !rowsBySku.size) {
    return result;
  }

  return {
    ...result,
    hits: result.hits.map((hit) => {
      const row = rowsBySku.get(hit.sku_id);
      return row ? applyPricingRow({ ...hit }, row) : hit;
    }),
  };
};

/**
 * Wrap the adapter's search client so hits are hydrated with the active store's
 * pricing before they reach InstantSearch, keeping the split collections
 * invisible to the components consuming hits. The store is read from the
 * `pricingStoreId` prop set on the `Configure` widget.
 */
export const withPricingHydration = (searchClient, typesenseClient) => ({
  ...searchClient,
  search: async (requests) => {
    const response = await searchClient.search(requests);
    if (!response || !Array.isArray(response.results)) {
      return response;
    }
    const results = await Promise.all(
      response.results.map((result, index) =>
        hydrateResult(result, requests[index], typesenseClient)
      )
    );
    return { ...response, results };
  },
});
